import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {
  deleteItemByID,
  doesLayoutItemHasDualFonts,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  showEditorNotificationMessageGrowl,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {openEditTableContentModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemLayoutModal} from '@Modals/poster-editor-item-layout-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorItemDateStylesModal} from '@Modals/poster-editor-item-date-styles-modal';
import {openPosterEditorItemFontStyleModal} from '@Modals/poster-editor-item-font-style-modal';
import {openPosterEditorItemFontStyle2Modal} from '@Modals/poster-editor-item-style-2-modal';
import {openPosterEditorItemFontSizeModal} from '@Modals/poster-editor-item-font-size-modal';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorTextOutlineModal} from '@Modals/poster-editor-text-outline-modal';
import {openPosterEditorLayoutItemBorderModal} from '@Modals/poster-editor-layout-item-border-modal';
import {openPosterEditorLayoutItemSpacingModal} from '@Modals/poster-editor-layout-item-spacing-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorLayoutFillModal} from '@Modals/poster-editor-layout-fill-modal';
import {openPosterEditorLayoutItemFontModal} from '@Modals/poster-editor-layout-item-font-modal';
import {useEditorPrefetchLayoutMobileModules} from '@Hooks/poster-editor/useEditorPrefetchLayoutItemMobileModules';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {openPosterEditorLineHeightModal} from '@Modals/poster-editor-item-line-height-modal';
import {TableItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {ItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/item-color-fill/item-color-fill';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';
import type {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {useGetNumberOfHighlightedRows} from '@Hooks/poster-editor/useGetNumberOfHighlightedRows';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './table-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function TableBottomBar(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  useEditorPrefetchLayoutMobileModules();
  const isBold = useGetKeyFromTextStylesActiveItem('isBold');
  const isBold2 = useGetKeyFromTableItem('isBold2');
  const layoutStyle = useGetKeyFromTableItem('layoutStyle') as LayoutTypes;
  const highlightedRows = useGetNumberOfHighlightedRows();
  const isLocked = useGetKeyFromActiveItem('lockMovementX');

  if (!activeItem?.isTableItem() || layoutStyle === undefined) {
    return null;
  }

  const showDisabledOutlineMessageGrowl = (): void => {
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_disabled_text_outline_mobile_text'));
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };
  const onClickHandler = (itemId: TableItemBottomBarOptions): void => {
    switch (itemId) {
      case TableItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case TableItemBottomBarOptions.CONTENT:
        openEditTableContentModal(activeItem);
        break;
      case TableItemBottomBarOptions.LAYOUT:
        openPosterEditorItemLayoutModal();
        break;
      case TableItemBottomBarOptions.FONT:
        openPosterEditorLayoutItemFontModal({
          panelId: 'poster-editor-layout-item-font-panel',
          fontFamilyOption: doesLayoutItemHasDualFonts(layoutStyle) ? FontFamilyOptionType.ONE : FontFamilyOptionType.ONLY_FONT,
        });
        break;
      case TableItemBottomBarOptions.STYLE:
        openPosterEditorItemFontStyleModal();
        break;
      case TableItemBottomBarOptions.FONT_TWO:
        openPosterEditorLayoutItemFontModal({
          panelId: 'poster-editor-layout-item-font-panel',
          fontFamilyOption: FontFamilyOptionType.TWO,
        });
        break;
      case TableItemBottomBarOptions.STYLE_TWO:
        openPosterEditorItemFontStyle2Modal();
        break;
      case TableItemBottomBarOptions.SIZE:
        openPosterEditorItemFontSizeModal();
        break;
      case TableItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case TableItemBottomBarOptions.FILL_DUAL_FONTS:
      case TableItemBottomBarOptions.FILL_SINGLE_FONT:
        openPosterEditorLayoutFillModal();
        break;
      case TableItemBottomBarOptions.OUTLINE:
        if (!(isBold || isBold2)) {
          showDisabledOutlineMessageGrowl();
          return;
        }
        openPosterEditorTextOutlineModal();
        break;
      case TableItemBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      case TableItemBottomBarOptions.SPACING:
        openPosterEditorLayoutItemSpacingModal();
        break;
      case TableItemBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case TableItemBottomBarOptions.LINE_HEIGHT:
        openPosterEditorLineHeightModal();
        break;
      case TableItemBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case TableItemBottomBarOptions.BORDER:
        openPosterEditorLayoutItemBorderModal();
        break;
      case TableItemBottomBarOptions.DATE_OR_HIGHLIGHTED_STYLES:
        openPosterEditorItemDateStylesModal();
        break;
      case TableItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case TableItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case TableItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case TableItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case TableItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      case TableItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      default:
        break;
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <ItemColorFill />;
  };

  const showHighlightStyles = (): boolean => {
    if (activeItem.isSchedule()) {
      return activeItem.doesItemHaveDateOptions();
    }

    return !!highlightedRows;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: TableItemBottomBarOptions.CONTENT,
          icon: 'icon-pencil',
          text: window.i18next.t('pmwjs_content'),
        },
        {
          id: TableItemBottomBarOptions.LAYOUT,
          icon: 'icon-layout',
          text: window.i18next.t('pmwjs_layout'),
          isHidden: !activeItem.isSchedule(),
        },
        {
          id: TableItemBottomBarOptions.FILL_DUAL_FONTS,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
          isHidden: !doesLayoutItemHasDualFonts(layoutStyle),
        },
        {
          id: TableItemBottomBarOptions.FONT,
          icon: doesLayoutItemHasDualFonts(layoutStyle) ? 'icon-font-1' : 'icon-font',
          text: doesLayoutItemHasDualFonts(layoutStyle) ? window.i18next.t('pmjws_font_one') : window.i18next.t('pmwjs_font'),
        },
        {
          id: TableItemBottomBarOptions.FILL_SINGLE_FONT,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
          isHidden: doesLayoutItemHasDualFonts(layoutStyle),
        },
        {
          id: TableItemBottomBarOptions.STYLE,
          icon: doesLayoutItemHasDualFonts(layoutStyle) ? 'icon-text-style-1' : 'icon-text-style',
          text: doesLayoutItemHasDualFonts(layoutStyle) ? window.i18next.t('pmwjs_style_one') : window.i18next.t('pmwjs_menu_stlye'),
        },
        {
          id: TableItemBottomBarOptions.FONT_TWO,
          icon: 'icon-font-2',
          text: window.i18next.t('pmjws_font_two'),
          isHidden: !doesLayoutItemHasDualFonts(layoutStyle),
        },
        {
          id: TableItemBottomBarOptions.STYLE_TWO,
          icon: 'icon-text-style-2',
          text: window.i18next.t('pmwjs_style_two'),
          isHidden: !doesLayoutItemHasDualFonts(layoutStyle),
        },
        {
          id: TableItemBottomBarOptions.SIZE,
          icon: 'icon-table-size',
          text: window.i18next.t('pmwjs_size'),
        },
        {
          id: TableItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: TableItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: TableItemBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isDisabled: !!(isBold ?? isBold2),
        },
        {
          id: TableItemBottomBarOptions.ALIGNMENT,
          icon: 'icon-h-align-center',
          text: window.i18next.t('pmwjs_alignment'),
          isHidden: activeItem.isSchedule(),
        },
        {
          id: TableItemBottomBarOptions.SPACING,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_spacing'),
        },
        {
          id: TableItemBottomBarOptions.LINE_HEIGHT,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_line_height'),
        },
        {
          id: TableItemBottomBarOptions.LETTER_SPACING,
          icon: 'icon-letter-spacing',
          text: window.i18next.t('pmwjs_letter_spacing'),
        },
        {
          id: TableItemBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: TableItemBottomBarOptions.BORDER,
          icon: 'icon-square',
          text: window.i18next.t('pmwjs_border'),
        },
        {
          id: TableItemBottomBarOptions.DATE_OR_HIGHLIGHTED_STYLES,
          icon: activeItem.isSchedule() ? 'icon-date-style' : 'icon-highlighter',
          text: activeItem.isSchedule() ? window.i18next.t('pmwjs_date_styles') : window.i18next.t('pmwjs_highlighted_items'),
          isHidden: !showHighlightStyles(),
          textClasses: 'spacing-p-0',
        },
        {
          id: TableItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: TableItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: TableItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: TableItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: TableItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: TableItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
